import { Inject, Injectable } from "@angular/core";
import { JwtHttpClientService } from "../http/jwt-http-client.service";
import { Observable, Subject } from "rxjs";
import { AbstractHttpService } from "../http/abstract-http.service";
import { Http } from "@angular/http";
import { AuthService } from "../auth/auth.service";
import { ConfigurationServiceConstants } from "./configuration-service-constants";
const deepAssign = require('deep-assign');
const stripJsonComments = require('strip-json-comments');
export class ConfigurationService extends AbstractHttpService {
    /**
     *
     * @param serverHttp Server HTTP Service
     * @param localHttp Local HTTP Service
     * @param authService Auth Service
     * @param application Application name used for context lookup
     * @param localConfigFile Path of local config file
     * @param contextNames Context names in priority order
     */
    constructor(serverHttp, localHttp, authService, application, localConfigFile, contextNames) {
        super();
        this.serverHttp = serverHttp;
        this.localHttp = localHttp;
        this.authService = authService;
        this.application = application;
        this.localConfigFile = localConfigFile;
        this.contextNames = contextNames;
        this.contexts = {};
        this._configurationChanges = new Subject();
        this.contexts["application"] = application;
        // Configuration details may be specific to the current user - need to refresh after a login/logout
        this.authService.loginChanging$.subscribe(() => {
            this.allConfigurations = null;
            this.allConfigurationsObservable = null;
        });
    }
    /**
     * Returns an observable sequence of changed config specs.
     * @returns {Observable}
     */
    get configurationChanges() {
        return this._configurationChanges;
    }
    setContext(contextName, value) {
        this.contexts[contextName] = value;
    }
    removeContext(contextName) {
        delete this.contexts[contextName];
    }
    getString(configSpec, dflt) {
        let match = ConfigurationServiceConstants.configSpecPattern.exec(configSpec);
        if (!match) {
            return Promise.resolve(null);
        }
        let category = match[1];
        let configName = match[2];
        return new Promise((resolve, reject) => {
            this.loadConfigurations().subscribe((configurations) => {
                const value = this.findConfigurationForContext(configurations, category, configName);
                resolve(value === undefined ? dflt : value);
            }, (error) => reject(error));
        });
    }
    getBoolean(configSpec, dflt = false) {
        return this.getString(configSpec).then((value) => value === undefined ? dflt : value.toLowerCase() === 'true');
    }
    getNumber(configSpec, dflt) {
        return this.getString(configSpec).then((value) => {
            if (value === undefined) {
                return dflt;
            }
            let i = parseInt(value);
            return isNaN(i) ? undefined : i;
        });
    }
    /**
     * Saves a configuration.
     * For a "local" configuration:
     *      The configuration is persisted in local browser-based storage.
     *      An array of context names may be provided, otherwise the default context is used.
     * For a server-based configuration:
     *      The context of the saved configuration is determined by the server configuration persister and returned from the service call.
     *      This context is then used to update the locally cached configuration map.
     * @param configSpec
     * @param value
     * @param contextNames
     * @param storeInSession Indicates that the value is stored only for the session. This is only relevant for local config
     * @returns A promise of a boolean (true on success) indicating that the configuration has been saved
     */
    saveString(configSpec, value, contextNames = [], storeInSession) {
        let match = ConfigurationServiceConstants.configSpecPattern.exec(configSpec);
        if (!match) {
            return Promise.resolve(false);
        }
        let category = match[1];
        let configName = match[2];
        if (category === 'local') {
            let contextSpec = contextNames.reduce((contextSpec, contextName) => {
                if (this.contexts[contextName]) {
                    contextSpec[contextName] = this.contexts[contextName];
                }
                return contextSpec;
            }, {});
            let contextSpecKey = contextSpec.length ?
                "{" + Object.keys(contextSpec).reduce((contextSpecKey, contextName) => (contextSpecKey ? contextSpecKey + ", " : "") + contextName + " = " + contextSpec[contextName], "") + "}" :
                "default";
            // update local storage
            const storage = storeInSession ? sessionStorage : localStorage;
            let localConfigurations = JSON.parse(storage.getItem(ConfigurationServiceConstants.LOCAL_STORAGE_KEY)) || {};
            ConfigurationServiceConstants.setConfiguration(localConfigurations, category, configName, contextSpecKey, contextSpec, value);
            storage.setItem(ConfigurationServiceConstants.LOCAL_STORAGE_KEY, JSON.stringify(localConfigurations));
            // update in-memory configs
            ConfigurationServiceConstants.setConfiguration(this.allConfigurations, category, configName, contextSpecKey, contextSpec, value);
            this._configurationChanges.next(configSpec);
            return Promise.resolve(true);
        }
        else {
            return this.serverHttp.put(ConfigurationService.URL_PREFIX + category + "/" + configName, value)
                .map((response) => {
                let contextSpecMap = response.json() || {};
                for (let contextSpecKey in contextSpecMap) {
                    if (!contextSpecMap.hasOwnProperty(contextSpecKey)) {
                        continue;
                    }
                    ConfigurationServiceConstants.setConfiguration(this.allConfigurations, category, configName, contextSpecKey, contextSpecMap[contextSpecKey], value);
                    this._configurationChanges.next(configSpec);
                }
                return true;
            })
                .catch((response) => this.handleServiceError(response))
                .toPromise();
        }
    }
    saveStringForUser(configSpec, value) {
        return this.saveString(configSpec, value, ['userid']);
    }
    saveBoolean(configSpec, value, contextNames) {
        return this.saveString(configSpec, value.toString(), contextNames);
    }
    saveBooleanForUser(configSpec, value) {
        return this.saveString(configSpec, value.toString(), ['userid']);
    }
    saveNumber(configSpec, value, contextNames) {
        return this.saveString(configSpec, value.toString(), contextNames);
    }
    saveNumberForBoolean(configSpec, value) {
        return this.saveString(configSpec, value.toString(), ['userid']);
    }
    /**
     * Returns an observable for the configuration data.
     * Multiple concurrent calls to this method will return the same observable, so that the data will not be fetched multiple times.
     * @returns {any}
     */
    loadConfigurations() {
        // Only allow a single concurrent request
        return this.allConfigurationsObservable ?
            this.allConfigurationsObservable :
            this.authService.isLoggedIn() ?
                this.loadAllConfigurations() :
                this.loadLocalConfigurations();
    }
    /**
     * Returns an observable for the merged configuration data from the server and the local configuration file.
     * Multiple concurrent calls to this method will return the same observable, so that the data will not be fetched multiple times.
     * @returns {any}
     */
    loadAllConfigurations() {
        // Only allow a single concurrent request
        if (this.allConfigurationsObservable) {
            return this.allConfigurationsObservable;
        }
        // Configurations are cached
        if (this.allConfigurations) {
            return Observable.of(this.allConfigurations);
        }
        let serverObservable = this.serverHttp.get(ConfigurationService.URL_PREFIX)
            .catch((response) => this.handleServiceError(response))
            .map((response) => response.json());
        let localObservable;
        if (this.localConfigFile) {
            localObservable = this.localHttp.get(this.localConfigFile)
                .catch((response) => this.handleServiceError(response))
                .map((response) => JSON.parse(stripJsonComments(response.text())));
        }
        else {
            localObservable = Observable.of({});
        }
        return this.allConfigurationsObservable = Observable.of(serverObservable, localObservable)
            .concatAll()
            .reduce((combinedConfigurations, configurations) => {
            if (combinedConfigurations == null) {
                // server configurations
                return configurations;
            }
            else {
                // local 'static' configurations
                for (let configName in configurations) {
                    if (!configurations.hasOwnProperty(configName)) {
                        continue;
                    }
                    ConfigurationServiceConstants.setConfiguration(combinedConfigurations, "local", configName, "default", { "": "" }, configurations[configName]);
                }
                // local storage (saved) configurations
                let localConfigurations = JSON.parse(localStorage.getItem(ConfigurationServiceConstants.LOCAL_STORAGE_KEY)) || {};
                deepAssign(combinedConfigurations, localConfigurations);
                // local session storage (saved) configurations
                localConfigurations = JSON.parse(sessionStorage.getItem(ConfigurationServiceConstants.LOCAL_STORAGE_KEY)) || {};
                deepAssign(combinedConfigurations, localConfigurations);
                return combinedConfigurations;
            }
        }, null)
            .do((configurations) => this.allConfigurations = configurations)
            .finally(() => this.allConfigurationsObservable = undefined)
            .share();
    }
    /**
     * Returns an observable for the configuration data from the local configuration file.
     * Multiple concurrent calls to this method will return the same observable, so that the data will not be fetched multiple times.
     * @returns {any}
     */
    loadLocalConfigurations() {
        // Only allow a single concurrent request
        if (this.localConfigurationsObservable) {
            return this.localConfigurationsObservable;
        }
        if (!this.localConfigFile) {
            return Observable.of({});
        }
        return this.localConfigurationsObservable = this.localHttp.get(this.localConfigFile)
            .catch((response) => this.handleServiceError(response))
            .map((response) => {
            let json = JSON.parse(stripJsonComments(response.text()));
            let configurations = {};
            for (let configName in json) {
                if (!json.hasOwnProperty(configName)) {
                    continue;
                }
                ConfigurationServiceConstants.setConfiguration(configurations, "local", configName, "default", { "": "" }, json[configName]);
            }
            // local storage (saved) configurations
            let localConfigurations = JSON.parse(localStorage.getItem(ConfigurationServiceConstants.LOCAL_STORAGE_KEY)) || {};
            deepAssign(configurations, localConfigurations);
            // local session storage (saved) configurations
            localConfigurations = JSON.parse(sessionStorage.getItem(ConfigurationServiceConstants.LOCAL_STORAGE_KEY)) || {};
            deepAssign(configurations, localConfigurations);
            return configurations;
        })
            .publishReplay(1)
            .refCount();
    }
    /**
     * Finds the configured value in the given configurations map for the given category and configName, which best matches the current context.
     * @param configurations configurations map loaded from the server.
     * @param category The category name for the config.
     * @param configName The config name for which a value is required.
     * @return {string} The best match config value.
     */
    findConfigurationForContext(configurations, category, configName) {
        if (!configurations.hasOwnProperty(category)) {
            return undefined;
        }
        let configurationMap = configurations[category];
        if (!configurationMap.hasOwnProperty(configName)) {
            return undefined;
        }
        let contextSpecMap = configurationMap[configName];
        let contextSpecs = configurations['contextSpecs'];
        let maxScore = 0;
        let config = contextSpecMap["default"];
        for (let contextSpecKey in contextSpecMap) {
            let score = this.scoreContextSpec(contextSpecs[contextSpecKey]);
            if (score > maxScore) {
                maxScore = score;
                config = contextSpecMap[contextSpecKey];
            }
        }
        return config;
    }
    /**
     * Assigns a score to the given context spec as matched against the current context.
     * A higher number signifies a more specific match.
     * @param contextSpec
     * @return {number} the score
     */
    scoreContextSpec(contextSpec) {
        // A context spec is not a match if it specifies a context name that has not been set to the same value in the current context
        return Object.keys(contextSpec).every((contextName) => !contextSpec[contextName] || this.contexts[contextName] === contextSpec[contextName]) ?
            // Compare each context spec value to the corresponding value in the current context, using the defined priority order.
            // If the current context has a value for a context name, but the context spec does not, then the sub-score is 0 for the context name, otherwise 1.
            this.contextNames
                .map((contextName) => this.contexts[contextName] && !contextSpec[contextName] ? 0 : 1)
                .reduce((score, subScore) => (score << 1) + subScore, 0) :
            0;
    }
}
/**
 * Static URL prefix for all server side calls
 * @type {string}
 */
ConfigurationService.URL_PREFIX = '/dataserver/configuration/';
ConfigurationService.decorators = [
    { type: Injectable },
];
/** @nocollapse */
ConfigurationService.ctorParameters = () => [
    { type: JwtHttpClientService, },
    { type: Http, },
    { type: AuthService, },
    { type: undefined, decorators: [{ type: Inject, args: [ConfigurationServiceConstants.APPLICATION_KEY,] },] },
    { type: undefined, decorators: [{ type: Inject, args: [ConfigurationServiceConstants.LOCAL_CONFIG_FILE_KEY,] },] },
    { type: Array, decorators: [{ type: Inject, args: [ConfigurationServiceConstants.CONTEXT_NAMES_KEY,] },] },
];
//# sourceMappingURL=configuration.service.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/@fedelta-pos/data-client/configuration/configuration.service.js
// module id = 36
// module chunks = 1